import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";

const STATUS_OPTS = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);
    const [editNote, setEditNote] = useState("");
    const [search, setSearch] = useState("");
    const searchRef = useRef("");

    useEffect(() => {
        if (active) setEditNote(active.note || "");
    }, [active]);

    const load = (p) => {
        const params = new URLSearchParams();
        if (filter !== "all") params.set("status", filter);
        if (searchRef.current.trim()) params.set("q", searchRef.current.trim());
        params.set("page", p);
        params.set("per_page", 20);

        api.get(`/admin/orders?${params}`).then((r) => {
            setOrders(r.data.items);
            setTotal(r.data.total);
            setPage(r.data.page);
            setTotalPages(r.data.total_pages);
        });
    };

    useEffect(() => { searchRef.current = search; }, [search]);

    useEffect(() => { load(1); }, []);

    const goPage = (p) => { setPage(p); load(p); };
    const goFilter = (s) => { setFilter(s); setPage(1); load(1); };

    const update = async (id, status) => {
        try {
            await api.put(`/admin/orders/${id}`, { status });
            toast.success("Updated");
            load(page);
            if (active?.id === id) setActive({ ...active, status });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div>
            <SEO title="Admin - Orders" />
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-xl sm:text-2xl font-bold text-brand-navy">Orders</h1>

                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center w-full sm:w-auto lg:w-[46%]">
                    <button
                        onClick={async () => {
                            try {
                                const r = await api.get("/admin/orders/export", { responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([r.data]));
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = "all_orders.xlsx";
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                            } catch (err) {
                                toast.error(formatApiError(err));
                            }
                        }}
                        className="px-3 py-1.5 text-xs border border-brand-light rounded-full flex items-center gap-1.5 whitespace-nowrap hover:bg-brand-light/50"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Export
                    </button>

                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { goPage(1); } }}
                        placeholder="Search order, customer, phone..."
                        className="w-full sm:max-w-[200px] px-3 py-1.5 text-xs border border-brand-light rounded-full outline-none"
                    />

                    <div className="flex flex-wrap gap-1.5">
                        {["all", ...STATUS_OPTS].map((s) => (
                            <button
                                key={s}
                                onClick={() => goFilter(s)}
                                className={`px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-medium ${
                                    filter === s ? "btn-primary" : "border border-brand-light"
                                }`}
                                data-testid={`filter-${s}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-brand-light overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-brand-light/40">
                        <tr>
                            <th className="text-left px-4 py-3">Order #</th>
                            <th className="text-left px-4 py-3">Customer</th>
                            <th className="text-left px-4 py-3">Phone</th>
                            <th className="text-left px-4 py-3">Total</th>
                            <th className="text-left px-4 py-3">Pay</th>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th></th>
                        </tr>
                    </thead>

                    <tbody>
                        {orders.map((o) => (
                            <tr key={o.id} className="border-t border-brand-light">
                                <td className="px-4 py-3 font-mono text-xs text-brand-royal">
                                    {o.order_number}
                                </td>
                                <td className="px-4 py-3 font-medium">{o.customer_name}</td>
                                <td className="px-4 py-3">{o.phone}</td>
                                <td className="px-4 py-3 font-semibold">৳{o.total}</td>
                                <td className="px-4 py-3 uppercase text-xs">{o.payment_method}</td>
                                <td className="px-4 py-3 text-xs">
                                    {o.created_at?.slice(0, 10)}
                                </td>
                                <td className="px-4 py-3">
                                    <Select value={o.status} onValueChange={(v) => update(o.id, v)}>
                                        <SelectTrigger className="h-8 w-32 text-xs">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTS.map((s) => (
                                                <SelectItem key={s} value={s}>
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="px-4 py-3">
                                    <button
                                        onClick={() => setActive(o)}
                                        className="p-1.5 hover:bg-brand-light/50 rounded"
                                    >
                                        <Eye className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {orders.length === 0 && (
                            <tr>
                                <td colSpan={8} className="text-center py-12 text-brand-navy/55">
                                    No orders found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                        onClick={() => goPage(Math.max(1, page - 1))}
                        disabled={page <= 1}
                        className="p-1.5 rounded border border-brand-light disabled:opacity-30 hover:bg-brand-light/50"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                        .map((p, idx, arr) => (
                            <span key={p} className="flex items-center gap-1">
                                {idx > 0 && arr[idx - 1] !== p - 1 && (
                                    <span className="px-1 text-brand-navy/40">...</span>
                                )}
                                <button
                                    onClick={() => goPage(p)}
                                    className={`min-w-[32px] h-8 rounded text-xs font-medium ${
                                        p === page
                                            ? "btn-primary"
                                            : "border border-brand-light hover:bg-brand-light/50"
                                    }`}
                                >
                                    {p}
                                </button>
                            </span>
                        ))}

                    <button
                        onClick={() => goPage(Math.min(totalPages, page + 1))}
                        disabled={page >= totalPages}
                        className="p-1.5 rounded border border-brand-light disabled:opacity-30 hover:bg-brand-light/50"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>

                    <span className="text-xs text-brand-navy/55 ml-2">
                        {total} total
                    </span>
                </div>
            )}

            <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
                <DialogContent className="max-w-2xl w-[95vw] sm:w-full font-bn p-3 sm:p-6 z-[100]">
                    {active && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-lg sm:text-xl flex items-center gap-3">
                                    {active.order_number}
                                    <button
                                        onClick={async () => {
                                            try {
                                                const r = await api.get(`/admin/orders/${active.id}/invoice`, { responseType: "blob" });
                                                const url = window.URL.createObjectURL(new Blob([r.data]));
                                                const a = document.createElement("a");
                                                a.href = url;
                                                a.download = `${active.order_number}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                document.body.removeChild(a);
                                                window.URL.revokeObjectURL(url);
                                            } catch (err) {
                                                toast.error(formatApiError(err));
                                            }
                                        }}
                                        className="inline-flex items-center gap-1.5 text-xs bg-brand-royal/10 text-brand-royal px-3 py-1 rounded-full hover:bg-brand-royal/20 transition-colors"
                                    >
                                        <Download className="w-3 h-3" />
                                        Invoice
                                    </button>
                                </DialogTitle>
                            </DialogHeader>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
                                <div><b>Name:</b> {active.customer_name}</div>
                                <div><b>Phone:</b> {active.phone}</div>
                                <div className="sm:col-span-2">
                                    <b>Address:</b> {active.address}, {active.zilla}, {active.division}
                                </div>

                                {active.library_name && (
                                    <div className="sm:col-span-2">
                                        <b>Library:</b> {active.library_name}
                                    </div>
                                )}

                                <div><b>Payment:</b> {active.payment_method}</div>
                                {active.bank_txn_id && <div><b>Txn ID:</b> {active.bank_txn_id}</div>}

                                <div className="sm:col-span-2">
                                    <b>Note:</b>
                                    <div className="flex gap-2 mt-1">
                                        <textarea
                                            value={editNote}
                                            onChange={(e) => setEditNote(e.target.value)}
                                            rows={2}
                                            className="flex-1 text-xs border border-brand-light rounded px-2 py-1 outline-none resize-none"
                                        />
                                        <button
                                            onClick={async () => {
                                                try {
                                                    await api.put(`/admin/orders/${active.id}`, {
                                                        status: active.status,
                                                        note: editNote,
                                                    });
                                                    toast.success("Note updated");
                                                    setActive({ ...active, note: editNote });
                                                    load(page);
                                                } catch (err) {
                                                    toast.error(formatApiError(err));
                                                }
                                            }}
                                            className="px-3 py-1 text-xs btn-primary rounded self-start"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-semibold mt-3 mb-2">Items</h4>
                                <div className="space-y-2">
                                    {active.items.map((it, i) => (
                                        <div
                                            key={i}
                                            className="flex justify-between text-sm border border-brand-light p-2 rounded-lg"
                                        >
                                            <div className="flex-1">
                                                {it.title} × {it.quantity}
                                            </div>
                                            <div className="font-semibold">
                                                ৳{it.price * it.quantity}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="border-t border-brand-light pt-3 space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span>Subtotal</span>
                                    <span>৳{active.subtotal}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Delivery</span>
                                    <span>৳{active.delivery_fee}</span>
                                </div>
                                <div className="flex justify-between font-bold text-brand-royal">
                                    <span>Total</span>
                                    <span>৳{active.total}</span>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
